// src/ConsumerApp/Screens/Account/views/SavedItemsView.js

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import BookmarkIcon from '../../../../assets/icons/bookmark.svg';
import FillBookmarkIcon from '../../../../assets/icons/FillBookmark.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';
import WhitePlus from '../../../../assets/icons/WhitePlus.svg';
import WhiteMinus from '../../../../assets/icons/WhiteMinus.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';

// API Hooks
import {
  useGetSavedList,
  useRemoveItemFromSavedList,
} from '../../../api/savedList/savedList.hooks';
import {
  useGetBucketSummary,
  useAddItemToBucket,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
} from '../../../api/products/product.hooks';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function SavedItemsView() {
  const navigate = useNavigate();

  // 1. Session Context (Direct Browser Storage)
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId || '';

  // 2. Data Queries
  const {
    data: savedItems = [],
    isLoading,
    refetch,
    isRefetching,
  } = useGetSavedList(customerId);

  const { data: bucketSummary } = useGetBucketSummary(customerId);

  // Track image load failures individually
  const [failedImages, setFailedImages] = useState({});

  // Mutations
  const removeItemMutation = useRemoveItemFromSavedList();
  const addItemMutation = useAddItemToBucket();
  const updateQtyMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();

  // 3. Bucket Items Quantity Map for Instant Steppers
  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const items = bucketSummary?.buy_now_bucket?.items || [];
    items.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: Number(bItem.quantity) || 1,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  // Handlers
  const handleRemoveSaved = (e, productId) => {
    e.stopPropagation();
    if (!customerId) return;
    removeItemMutation.mutate({ customer_id: customerId, product_id: productId });
  };

  const handleAddToCart = (e, item) => {
    e.stopPropagation();
    if (!customerId) {
      navigate('/consumer');
      return;
    }

    const productId = String(item.product_id || item.item_id || item._id);
    const minQty = item.quantity_limits?.min || item.buy_now?.min_qty || 1;

    addItemMutation.mutate({
      customer_id: customerId,
      hubId,
      item_id: productId,
      purchase_type: 'BUY_NOW',
      quantity: minQty,
    });
  };

  const handleUpdateQty = (e, productId, newQty, bucketId) => {
    e.stopPropagation();
    if (newQty <= 0) {
      deleteItemMutation.mutate({
        customer_id: customerId,
        item_id: productId,
        bucket_id: bucketId,
      });
    } else {
      updateQtyMutation.mutate({
        customer_id: customerId,
        hubId,
        item_id: productId,
        purchase_type: 'BUY_NOW',
        quantity: newQty,
      });
    }
  };

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>Saved Items</h2>
          <span style={styles.headerSubtitle}>
            Review your shortlisted materials and move items directly into your bucket
          </span>
        </div>

      {/*   <button
          type="button"
          style={styles.refreshBtn}
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <img
            src={ClockCounterClockwise}
            alt="refresh"
            style={{
              width: 14,
              height: 14,
              opacity: 0.7,
              animation: isRefetching ? 'spin 1s linear infinite' : 'none',
            }}
          />
          <span>{isRefetching ? 'Refreshing...' : 'Refresh'}</span>
        </button> */}
      </div>

      {/* ── Content Grid ── */}
      {isLoading ? (
        <div style={styles.loadingBox}>
          <span>Loading saved materials...</span>
        </div>
      ) : savedItems.length === 0 ? (
        <div style={styles.emptyBox}>
          <div style={styles.emptyIconCircle}>
            <img
              src={FillBookmarkIcon}
              alt="saved"
              style={{
                width: 24,
                height: 24,
                filter:
                  'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
              }}
            />
          </div>
          <h4 style={styles.emptyTitle}>Your saved list is empty</h4>
          <p style={styles.emptySubtitle}>
            Shortlist products to order them quickly.
          </p>
          <button
            type="button"
            style={styles.exploreBtn}
            onClick={() => navigate('/consumer/HomeScreen')}
          >
            Explore
          </button>
        </div>
      ) : (
        <div style={styles.productGrid}>
          {savedItems.map((item, index) => {
            const productId = String(item.product_id || item.item_id || item._id);
            const name = item.product_name || item.item_name || 'Construction Material';
            const brand = item.brand || '';
            const unitPrice = Number(item.selling_price || item.price || item.unit_price || 0);
            const mrp = Number(item.mrp || 0);
            const uom = item.uom || item.uom_name || item.UOM || 'unit';

            const rawImg =
              item.product_image_url ||
              item.product_image ||
              item.product_image_stored_name ||
              item.item_image;
            const imageUrl = rawImg
              ? rawImg.startsWith('http')
                ? rawImg
                : `${IMAGE_BASE_URL}${rawImg}`
              : null;

            const isImgBroken = failedImages[productId];
            const bucketEntry = bucketItemsMap.get(productId);
            const inCartQty = bucketEntry ? bucketEntry.quantity : 0;

            return (
              <div
                key={`${productId}-${index}`}
                style={styles.productCard}
                onClick={() => navigate(`/consumer/productdetails`, { state: { productId } })}
              >
                {/* Image Frame & Bookmark Trigger */}
                <div style={styles.imageFrame}>
                  {imageUrl && !isImgBroken ? (
                    <img
                      src={imageUrl}
                      alt={name}
                      style={styles.productImg}
                      onError={() =>
                        setFailedImages((prev) => ({ ...prev, [productId]: true }))
                      }
                    />
                  ) : (
                    <div style={styles.fallbackBox}>
                      <img src={ToteIcon} alt="fallback" style={styles.toteFallback} />
                    </div>
                  )}

                  <button
                    type="button"
                    style={styles.bookmarkBtn}
                    onClick={(e) => handleRemoveSaved(e, productId)}
                    title="Remove from saved"
                  >
                    <img
                      src={FillBookmarkIcon}
                      alt="saved"
                      style={{
                        width: 16,
                        height: 16,
                        filter:
                          'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                      }}
                    />
                  </button>
                </div>

                {/* Details */}
                <div style={styles.cardBody}>
                  {brand && <span style={styles.brandText}>{brand}</span>}
                  <h4 style={styles.titleText} title={name}>
                    {name}
                  </h4>
                  <span style={styles.unitText}>Per {uom}</span>

                  {/* Price & Action Row */}
                  <div style={styles.priceActionRow}>
                    <div style={styles.priceCol}>
                      <span style={styles.priceText}>₹{unitPrice.toLocaleString('en-IN')}</span>
                      {mrp > unitPrice && (
                        <span style={styles.mrpText}>₹{mrp.toLocaleString('en-IN')}</span>
                      )}
                    </div>

                    {/* Add / Zepto Red Stepper */}
                    {inCartQty > 0 ? (
                      <div style={styles.zeptoStepper} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          style={styles.stepperActionBtn}
                          onClick={(e) =>
                            handleUpdateQty(e, productId, inCartQty - 1, bucketEntry.bucketId)
                          }
                          aria-label="Decrease quantity"
                        >
                          <img src={WhiteMinus} alt="-" style={{ width: 16, height: 16 }} />
                        </button>
                        <span style={styles.stepperCount}>{inCartQty}</span>
                        <button
                          type="button"
                          style={styles.stepperActionBtn}
                          onClick={(e) =>
                            handleUpdateQty(e, productId, inCartQty + 1, bucketEntry.bucketId)
                          }
                          aria-label="Increase quantity"
                        >
                          <img src={WhitePlus} alt="+" style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        style={styles.addToCartBtn}
                        onClick={(e) => handleAddToCart(e, item)}
                      >
                        ADD TO BUCKET
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    cursor: 'pointer',
    outline: 'none',
  },
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 16,
  },
  productCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  },
  imageFrame: {
    height: 160,
    backgroundColor: '#F8FAFC',
    borderBottom: '1px solid #F1F5F9',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  productImg: {
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  fallbackBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toteFallback: {
    width: 36,
    height: 36,
    opacity: 0.35,
  },
  bookmarkBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  cardBody: {
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 4,
  },
  brandText: {
    fontSize: 10.5,
    fontWeight: '700',
    color: '#707B8C',
    textTransform: 'uppercase',
  },
  titleText: {
    margin: 0,
    fontSize: 14,
    fontWeight: '700',
    color: '#2B2A29',
    lineHeight: '18px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    minHeight: 36,
  },
  unitText: {
    fontSize: 11.5,
    color: '#707B8C',
    marginBottom: 8,
  },
  priceActionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: 8,
    borderTop: '1px solid #F8FAFC',
  },
  priceCol: {
    display: 'flex',
    flexDirection: 'column',
  },
  priceText: {
    fontSize: 15,
    fontWeight: '800',
    color: '#2B2A29',
  },
  mrpText: {
    fontSize: 11,
    color: '#707B8C',
    textDecoration: 'line-through',
  },
  /* Compact Non-Heavy Add to Bucket Button */
  addToCartBtn: {
    backgroundColor: '#FFFFFF',
    color: '#E9001E',
    border: '1px solid #E9001E',
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 11.5,
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: '0 2px 4px rgba(233,0,30,0.12)',
    letterSpacing: '0.4px',
  },
  /* Solid Bright Red Zepto Stepper */
  zeptoStepper: {
    backgroundColor: '#E9001E',
    border: 'none',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px',
    width: 100,
    height: 32,
    boxSizing: 'border-box',
    boxShadow: '0 2px 6px rgba(233, 0, 30, 0.28)',
  },
  stepperActionBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    width: 24,
    height: '100%',
  },
  stepperCount: {
    fontSize: 13,
    fontWeight: '800',
    color: '#FFFFFF',
    userSelect: 'none',
  },
  loadingBox: {
    padding: '50px 0',
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13.5,
  },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '6px 0 16px 0',
    fontSize: 12.5,
    color: '#707B8C',
    maxWidth: 320,
    lineHeight: '18px',
  },
  exploreBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
};